// Convite de equipe pendente (link ?team= / deep link), guardado até o usuário
// concluir cadastro ou login.
//
// PROBLEMA QUE ISTO RESOLVE — "cliquei no link da equipe X e caí na equipe Y":
// a leitura antiga era `teamJoin?.teamCode || teamCode`, ou seja, o valor
// GUARDADO tinha precedência sobre o `?team=` da URL que a pessoa acabou de
// abrir. Quem clicava num convite, desistia no meio e depois abria outro link
// era vinculado pelo primeiro. Pior: um link SEM `?team=` também aplicava o
// convite velho, porque o guardado nunca expirava (o capturedAt era gravado e
// nunca lido).
//
// Regras agora:
//   1. A URL ATUAL sempre vence. O guardado é só para o caso de o convite se
//      perder na navegação (cadastro/OTP acontecem em outra tela).
//   2. O convite guardado expira (VALIDADE_MS). Convite velho não vincula.
//   3. O convite guarda a academia de origem: se a academia da tela não é a
//      mesma da captura, o guardado é descartado. Um JOIN_CODE só é único
//      DENTRO de uma academia, então aplicar o código de outra academia é
//      justamente como se cai na equipe errada.

const CHAVE = "LESVITA_TEAM_JOIN";

/**
 * Status do backend que significam "o vínculo de equipe está valendo agora".
 *
 * FONTE ÚNICA de propósito: esta lista já existiu duplicada como uma cadeia de
 * `||` dentro da modal, e quando o backend passou a devolver "switched" a troca
 * gravava mas a tela dizia que tinha falhado. Quem adicionar um status novo
 * mexe aqui e todos os consumidores acompanham.
 *
 *   linked    — primeiro vínculo
 *   unchanged — já estava nesta equipe (resposta idempotente)
 *   switched  — trocou de equipe; os pontos migraram junto
 */
export const STATUS_VINCULO_OK = ["linked", "unchanged", "switched"];

/** Evento global emitido quando um vínculo/troca de equipe se resolve. */
export const EVENTO_VINCULO_EQUIPE = "lesvita:team-join-result";

/** true se o resultado do backend representa vínculo ativo. */
export function vinculoDeuCerto(resultado) {
  return STATUS_VINCULO_OK.includes(resultado?.status);
}

// 24h: cobre folgadamente cadastro + OTP + eventual troca de aparelho no mesmo
// dia, sem deixar um convite de semanas atrás vinculando ninguém.
export const VALIDADE_MS = 24 * 60 * 60 * 1000;

function normalizaGym(v) {
  return String(v || "").trim().toLowerCase();
}

/** Grava o convite pendente. Chame só quando o link REALMENTE traz equipe. */
export function salvarConvitePendente({ teamCode, gymCode, competitionId, teamId }) {
  if (!teamCode && !teamId) return;
  try {
    localStorage.setItem(CHAVE, JSON.stringify({
      teamCode: teamCode || null,
      teamId: teamId || null,
      competitionId: competitionId || null,
      gymCode: normalizaGym(gymCode),
      capturedAt: Date.now(),
    }));
  } catch (_e) { /* storage cheio/bloqueado não pode derrubar o fluxo */ }
}

/** Lê o convite guardado, já validado por prazo e academia. null se inválido. */
export function lerConvitePendente({ gymCode } = {}) {
  let bruto = null;
  try {
    bruto = JSON.parse(localStorage.getItem(CHAVE) || "null");
  } catch (_e) {
    return null;
  }
  if (!bruto) return null;

  const idade = Date.now() - Number(bruto.capturedAt || 0);
  if (!bruto.capturedAt || idade > VALIDADE_MS) {
    limparConvitePendente();
    return null;
  }

  // Convite de OUTRA academia não vale para esta tela.
  const gymAtual = normalizaGym(gymCode);
  if (gymAtual && bruto.gymCode && bruto.gymCode !== gymAtual) return null;

  return bruto;
}

export function limparConvitePendente() {
  try {
    localStorage.removeItem(CHAVE);
  } catch (_e) { /* idem */ }
}

/**
 * Convite EFETIVO da tela: a URL atual vence; o guardado é fallback.
 *
 * @param {object} daUrl        {teamCode, teamId, competitionId} lidos da URL
 * @param {string} gymCode      academia da tela (desambigua o guardado)
 * @returns {object|null}       {teamCode, teamId, competitionId, origem}
 */
export function resolverConvite(daUrl = {}, gymCode = null) {
  if (daUrl.teamId || daUrl.teamCode) {
    return {
      teamCode: daUrl.teamCode || null,
      teamId: daUrl.teamId || null,
      competitionId: daUrl.competitionId || null,
      origem: "url",
    };
  }
  const guardado = lerConvitePendente({ gymCode });
  if (!guardado) return null;
  return {
    teamCode: guardado.teamCode || null,
    teamId: guardado.teamId || null,
    competitionId: guardado.competitionId || null,
    origem: "storage",
  };
}
